import { motion } from "framer-motion";
import { BadgeEuro, Home, MessageCircle, ShieldCheck, Users } from "lucide-react";
import { IMAGES } from "@/lib/constants";

const ROWS = [
  {
    icon: Home,
    title: "Alquiler y venta",
    text: "Pisos y casas en alquiler o en venta: tú eliges lo que mejor te encaje, en cualquier rincón de España.",
    testid: "why-card-alquiler-venta",
  },
  {
    icon: BadgeEuro,
    title: "Mejores precios",
    text: "Seleccionamos cada inmueble para ofrecerte precios que de verdad merecen la pena.",
    testid: "why-card-precios",
  },
  {
    icon: ShieldCheck,
    title: "Anuncios propios",
    text: "Solo nuestro equipo sube y gestiona los inmuebles. Sin terceros, sin anuncios duplicados y sin sorpresas.",
    testid: "why-card-propios",
  },
  {
    icon: MessageCircle,
    title: "Trato directo por WhatsApp",
    text: "Hablas con nosotros directamente, sin intermediarios ni esperas.",
    testid: "why-card-whatsapp",
  },
  {
    icon: Users,
    title: "Todo en persona",
    text: "No pedimos dinero ni documentación por adelantado. Todo se hace en persona, como debe ser.",
    testid: "why-card-persona",
  },
];

export default function WhyNidoria() {
  return (
    <section id="por-que-nidoria" className="scroll-mt-24 bg-white py-20 dark:bg-[#0a1522] sm:py-28" data-testid="why-section">
      <div className="mx-auto grid max-w-6xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-[1.05fr_0.95fr]">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
          >
            <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-[#0f766e] dark:text-[#14b8a6]">Nuestras ventajas</p>
            <h2 className="mt-3 font-serif text-3xl font-semibold text-[#12263a] dark:text-[#f7f4ee] sm:text-4xl">
              Por qué Nidoria
            </h2>
            <p className="mt-4 max-w-lg text-base text-[#12263a]/65 dark:text-[#f7f4ee]/60 sm:text-lg">
              Un portal inmobiliario hecho para que encontrar casa sea fácil, seguro y sin letra pequeña.
            </p>
          </motion.div>

          <div className="mt-10 space-y-6">
            {ROWS.map((row, i) => (
              <motion.div
                key={row.title}
                initial={{ opacity: 0, x: -24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
                className="group flex items-start gap-4 border-b border-[#12263a]/10 pb-6 last:border-0 last:pb-0 dark:border-white/10"
                data-testid={row.testid}
              >
                <span className="shrink-0 rounded-2xl bg-[#0f766e]/10 p-3 text-[#0f766e] transition-colors duration-300 group-hover:bg-[#0f766e] group-hover:text-white dark:bg-[#14b8a6]/10 dark:text-[#14b8a6] dark:group-hover:bg-[#14b8a6] dark:group-hover:text-[#07332f]">
                  <row.icon className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="font-serif text-lg font-semibold text-[#12263a] dark:text-[#f7f4ee]">{row.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-[#12263a]/60 dark:text-[#f7f4ee]/55">{row.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, x: 32 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto w-full max-w-md"
        >
          <div className="absolute -inset-3 -rotate-2 rounded-[2.4rem] border-2 border-[#14b8a6]/40" aria-hidden="true" />
          <div className="relative rotate-1 overflow-hidden rounded-[2rem] shadow-2xl">
            <motion.img
              initial={{ scale: 1.12 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
              src={IMAGES.interior}
              alt="Interior luminoso de una vivienda gestionada por Nidoria"
              className="aspect-[5/6] w-full object-cover"
              loading="lazy"
              data-testid="why-interior-photo"
            />
          </div>
          <div className="absolute -bottom-6 -left-2 rounded-2xl bg-[#12263a] p-5 shadow-2xl sm:-left-8" data-testid="why-stat-100">
            <p className="font-serif text-4xl font-semibold text-[#14b8a6]">
              100<span className="text-[#f59e0b]">%</span>
            </p>
            <p className="mt-1 max-w-[190px] text-xs font-semibold leading-snug text-white/85">
              anuncios gestionados por nuestro equipo
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
